/**
 * The wall palette and the floor catalogue by name only. Nothing here imports
 * PlayCanvas, so the UI and the session data can share it and it tests
 * headless.
 */

/**
 * The floor styles the artist authored. A style's material asset is named
 * `<Name>_Scene-03`; that naming is the contract with the artist.
 */
export const floorStyleNames = [
	"ConcreteSmoothSemigloss",
	"TilesSand",
	"TilesGreyDark",
	"FabricCarpetFuzzy-Beige",
	"FabricCarpetFuzzy-Grey",
	"WoodParquetOak-1-Stav",
	"WoodParquetOak-1-Stav-White",
	"WoodParquetOak-1-Stav-BrownDark",
	"WoodParquetOak-3-Stav",
	"WoodParquetOak-3-Stav-White",
	"WoodParquetOak-3-Stav-BrownDark",
] as const;

export type FloorStyleName = (typeof floorStyleNames)[number];

export const defaultFloorStyle: FloorStyleName = "ConcreteSmoothSemigloss";

export function floorStyleAssetName(name: FloorStyleName): string {
	return `${name}_Scene-03`;
}

export function isFloorStyleName(value: unknown): value is FloorStyleName {
	return floorStyleNames.includes(value as FloorStyleName);
}

/** Floor styles too dark for the measurements drawn on them to stay dark. */
export const darkFloorStyleNames: readonly FloorStyleName[] = [
	"TilesGreyDark",
	"WoodParquetOak-1-Stav-BrownDark",
	"WoodParquetOak-3-Stav-BrownDark",
];

export function isDarkFloorStyle(name: FloorStyleName): boolean {
	return darkFloorStyleNames.includes(name);
}

/** Colour for measurements and text drawn over a dark floor. */
export const darkFloorForegroundColor = "#cccccc";

/** The artist's ten wall tints, the `diffuse` of each `Wall-<Name>_Scene-03`. */
export const wallColors = {
	DeepBlueGrey: "6F7D8A",
	DustyBlue: "8F9BA6",
	DustyPink: "C59B91",
	Greige: "BCB6A4",
	OliveGreen: "888E7D",
	SageGreen: "A7B09B",
	SandBeige: "C3AF93",
	Taupe: "91877E",
	Terracotta: "BA927D",
	WarmOffWhite: "D3CBB5",
} as const;

export type WallColorName = keyof typeof wallColors;

export const wallColorNames = Object.keys(wallColors) as WallColorName[];

export const defaultWallColor: WallColorName = "WarmOffWhite";

export function isWallColorName(value: unknown): value is WallColorName {
	return wallColorNames.includes(value as WallColorName);
}

export type RoomAppearance = {
	wallColor: WallColorName;
	floorStyle: FloorStyleName;
};

export const defaultRoomAppearance: RoomAppearance = {
	wallColor: defaultWallColor,
	floorStyle: defaultFloorStyle,
};

/**
 * Reads a room appearance out of stored data, falling back to the defaults
 * for anything missing or unknown, so old or hand-edited designs open.
 */
export function roomAppearanceFromData(data: unknown): RoomAppearance {
	const room = (data ?? {}) as Partial<Record<keyof RoomAppearance, unknown>>;
	return {
		wallColor: isWallColorName(room.wallColor)
			? room.wallColor
			: defaultWallColor,
		floorStyle: isFloorStyleName(room.floorStyle)
			? room.floorStyle
			: defaultFloorStyle,
	};
}
